import {
   calculateFilterDetune,
   calculateFilterGain,
   calculateFilterQ,
} from '../../util';

const filterDefaultFreq = 32;

export const getEqCurvePath = (
   canvasWidth,
   canvasHeight,
   Fs,
   minDB,
   maxDB,
   filterArr
) => {
   let path = '';

   const canvasMidHeight = canvasHeight / 2;
   for (let i = -10; i < canvasWidth + 10; i += 1) {
      // draw beyond canvas so curve looks nicer
      let dBMagnitude = 1;

      // frequencies from 20 to 20480 are drawn across length of canvas
      const wi = (20 * 2 ** ((i / canvasWidth) * 10) * 2 * Math.PI) / Fs;
      for (let j = 0; j < filterArr.length; j += 1) {
         const currentFilter = filterArr[j];

         const gain = calculateFilterGain(currentFilter.gain.value);
         const detune = calculateFilterDetune(currentFilter.detune.value);
         const Q = calculateFilterQ(
            currentFilter.Q.value,
            currentFilter.type.value
         );

         const A = 10 ** (gain / 40);
         const w0 =
            (Math.PI * 2 * (filterDefaultFreq * 2 ** (detune / 1200))) / Fs;
         const aQ = Math.sin(w0) / (2 * Q);
         let aB =
            (Math.sin(w0) / 2) *
            Math.sqrt((4 - Math.sqrt(16 - 16 / gain ** 2)) / 2);
         const S = 1;
         const aS =
            (Math.sin(w0) / 2) * Math.sqrt((A + 1 / A) * (1 / S - 1) + 2);
         let a0;
         let a1;
         let a2;
         let b0;
         let b1;
         let b2;
         let G;
         switch (currentFilter.type.value) {
            case 'peaking':
               b0 = 1 + aQ * A;
               b1 = -2 * Math.cos(w0);
               b2 = 1 - aQ * A;
               a0 = 1 + aQ / A;
               a1 = -2 * Math.cos(w0);
               a2 = 1 - aQ / A;
               break;
            case 'lowpass':
               G = 10 ** (0.05 * Q);
               aB =
                  (Math.sin(w0) / 2) *
                  Math.sqrt((4 - Math.sqrt(16 - 16 / G ** 2)) / 2);
               b0 = (1 - Math.cos(w0)) / 2;
               b1 = 1 - Math.cos(w0);
               b2 = (1 - Math.cos(w0)) / 2;
               a0 = 1 + aB;
               a1 = -2 * Math.cos(w0);
               a2 = 1 - aB;
               break;
            case 'highpass':
               G = 10 ** (0.05 * Q);
               aB =
                  (Math.sin(w0) / 2) *
                  Math.sqrt((4 - Math.sqrt(16 - 16 / G ** 2)) / 2);
               b0 = (1 + Math.cos(w0)) / 2;
               b1 = -(1 + Math.cos(w0));
               b2 = (1 + Math.cos(w0)) / 2;
               a0 = 1 + aB;
               a1 = -2 * Math.cos(w0);
               a2 = 1 - aB;
               break;
            case 'lowshelf':
               b0 =
                  A * (A + 1 - (A - 1) * Math.cos(w0) + 2 * aS * Math.sqrt(A));
               b1 = 2 * A * (A - 1 - (A + 1) * Math.cos(w0));
               b2 =
                  A * (A + 1 - (A - 1) * Math.cos(w0) - 2 * aS * Math.sqrt(A));
               a0 = A + 1 + (A - 1) * Math.cos(w0) + 2 * aS * Math.sqrt(A);
               a1 = -2 * (A - 1 + (A + 1) * Math.cos(w0));
               a2 = A + 1 + (A - 1) * Math.cos(w0) - 2 * aS * Math.sqrt(A);
               break;
            case 'highshelf':
               b0 =
                  A * (A + 1 + (A - 1) * Math.cos(w0) + 2 * aS * Math.sqrt(A));
               b1 = -2 * A * (A - 1 + (A + 1) * Math.cos(w0));
               b2 =
                  A * (A + 1 + (A - 1) * Math.cos(w0) - 2 * aS * Math.sqrt(A));
               a0 = A + 1 - (A - 1) * Math.cos(w0) + 2 * aS * Math.sqrt(A);
               a1 = 2 * (A - 1 - (A + 1) * Math.cos(w0));
               a2 = A + 1 - (A - 1) * Math.cos(w0) - 2 * aS * Math.sqrt(A);
               break;
            case 'bandpass':
               b0 = aQ;
               b1 = 0;
               b2 = -aQ;
               a0 = 1 + aQ;
               a1 = -2 * Math.cos(w0);
               a2 = 1 - aQ;
               break;
            case 'notch':
               b0 = 1;
               b1 = -2 * Math.cos(w0);
               b2 = 1;
               a0 = 1 + aQ;
               a1 = -2 * Math.cos(w0);
               a2 = 1 - aQ;
               break;
            default:
               break;
         }
         b0 /= a0;
         b1 /= a0;
         b2 /= a0;
         a1 /= a0;
         a2 /= a0;
         a0 = 1;

         const numerator =
            b0 * b0 +
            b1 * b1 +
            b2 * b2 +
            2 * (b0 * b1 + b1 * b2) * Math.cos(wi) +
            2 * b0 * b2 * Math.cos(2 * wi);
         const denominator =
            1 +
            a1 * a1 +
            a2 * a2 +
            2 * (a1 + a1 * a2) * Math.cos(wi) +
            2 * a2 * Math.cos(2 * wi);
         dBMagnitude *= Math.sqrt(numerator / denominator);
      }
      dBMagnitude = 20 * Math.log10(dBMagnitude);
      if (i === -10)
         path += `M ${i} ${
            canvasMidHeight - (dBMagnitude / (maxDB - minDB)) * canvasHeight
         }`;
      else
         path += ` L ${i} ${
            canvasMidHeight - (dBMagnitude / (maxDB - minDB)) * canvasHeight
         }`;
   }
   return path;
};
